/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const csCZ: LocalizationResource = {
  locale: 'cs-CZ',
  apiKeys: {
    action__add: 'Přidat nový klíč',
    action__search: 'Vyhledat klíče',
    createdAndExpirationStatus__expiresOn:
      "Vytvořeno {{ createdDate | shortDate('cs-CZ') }} • Platí do {{ expiresDate | longDate('cs-CZ') }}",
    createdAndExpirationStatus__never: "Vytvořeno {{ createdDate | shortDate('cs-CZ') }} • Nikdy nevyprší",
    detailsTitle__emptyRow: 'Nenalezeny žádné API klíče',
    formButtonPrimary__add: 'Vytvořit klíč',
    formFieldCaption__expiration__expiresOn: 'Vyprší {{ date }}',
    formFieldCaption__expiration__never: 'Tento klíč nikdy nevyprší',
    formFieldOption__expiration__180d: '180 dní',
    formFieldOption__expiration__1d: '1 den',
    formFieldOption__expiration__1y: '1 rok',
    formFieldOption__expiration__30d: '30 dní',
    formFieldOption__expiration__60d: '60 dní',
    formFieldOption__expiration__7d: '7 dní',
    formFieldOption__expiration__90d: '90 dní',
    formFieldOption__expiration__never: 'Nikdy',
    formHint: 'Zadejte název pro vygenerování nového klíče. Budete ho moci kdykoli zrušit.',
    formTitle: 'Přidat nový API klíč',
    lastUsed__days: 'Před {{days}} dny',
    lastUsed__hours: 'Před {{hours}} hodinami',
    lastUsed__minutes: 'Před {{minutes}} minutami',
    lastUsed__months: 'Před {{months}} měsíci',
    lastUsed__seconds: 'Před {{seconds}} sekundami',
    lastUsed__years: 'Před {{years}} lety',
    menuAction__revoke: 'Zrušit klíč',
    revokeConfirmation: {
      confirmationText: 'Zrušit',
      formButtonPrimary__revoke: 'Zrušit klíč',
      formHint: 'Jste si jisti, že chcete smazat tento tajný klíč?',
      formTitle: 'Zrušit tajný klíč "{{apiKeyName}}"?',
    },
  },
  backButton: 'Zpět',
  badge__activePlan: 'Aktivní',
  badge__canceledEndsAt: "Zrušeno • Končí {{ date | shortDate('cs-CZ') }}",
  badge__currentPlan: 'Aktuální plán',
  badge__default: 'Výchozí',
  badge__endsAt: "Končí {{ date | shortDate('cs-CZ') }}",
  badge__expired: 'Vypršelo',
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Jiné zařízení pro simulaci',
  badge__pastDueAt: "Po splatnosti {{ date | shortDate('cs-CZ') }}",
  badge__pastDuePlan: 'Po splatnosti',
  badge__primary: 'Hlavní',
  badge__renewsAt: "Obnovuje se {{ date | shortDate('cs-CZ') }}",
  badge__requiresAction: 'Vyžaduje akci',
  badge__startsAt: "Začíná {{ date | shortDate('cs-CZ') }}",
  badge__thisDevice: 'Toto zařízení',
  badge__trialEndsAt: undefined,
  badge__unverified: 'Nepotvrzené',
  badge__upcomingPlan: 'Nadcházející',
  badge__userDevice: 'Zařízení uživatele',
  badge__you: 'Vy',
  commerce: {
    addPaymentMethod: 'Přidat platební metodu',
    alwaysFree: 'Vždy zdarma',
    annually: 'Ročně',
    availableFeatures: 'Dostupné funkce',
    billedAnnually: 'Fakturováno ročně',
    billedMonthlyOnly: 'Fakturováno pouze měsíčně',
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: 'Zrušit předplatné',
    cancelSubscriptionAccessUntil:
      "Funkce '{{plan}}' můžete používat do {{ date | longDate('cs-CZ') }}, poté k nim ztratíte přístup.",
    cancelSubscriptionNoCharge: 'Za toto předplatné vám nebudou účtovány žádné poplatky.',
    cancelSubscriptionTitle: 'Zrušit předplatné {{plan}}?',
    cannotSubscribeMonthly:
      'Nelze se přihlásit k tomuto plánu s měsíční platbou. Abyste se k němu přihlásili, musíte zvolit roční platbu.',
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: 'Vaše platba byla úspěšná.',
      description__subscriptionSuccessful: 'Vaše nové předplatné je nastaveno.',
      downgradeNotice:
        'Současné předplatné a jeho funkce si ponecháte do konce fakturačního cyklu, poté budete převedeni na toto předplatné.',
      emailForm: {
        subtitle: 'Než budete moci dokončit nákup, musíte přidat e-mailovou adresu, na kterou budou zasílány účtenky.',
        title: 'Přidat e-mailovou adresu',
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: 'Platební metoda',
        title__statementId: 'ID výpisu',
        title__subscriptionBegins: 'Předplatné začíná',
        title__totalPaid: 'Celkem zaplaceno',
      },
      pastDueNotice: 'Vaše předchozí předplatné bylo po splatnosti, bez platby.',
      perMonth: 'měsíčně',
      title: 'Pokladna',
      title__paymentSuccessful: 'Platba byla úspěšná!',
      title__subscriptionSuccessful: 'Úspěch!',
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: 'Kredit',
    creditRemainder: 'Kredit za zbytek vašeho současného předplatného.',
    defaultFreePlanActive: 'Aktuálně používáte bezplatný plán',
    free: 'Zdarma',
    getStarted: 'Začít',
    keepFreeTrial: undefined,
    keepSubscription: 'Ponechat předplatné',
    manage: 'Spravovat',
    manageSubscription: 'Spravovat předplatné',
    month: 'Měsíc',
    monthly: 'Měsíčně',
    pastDue: 'Po splatnosti',
    pay: 'Zaplatit {{amount}}',
    paymentMethods: 'Platební metody',
    paymentSource: {
      applePayDescription: {
        annual: 'Roční platba',
        monthly: 'Měsíční platba',
      },
      dev: {
        anyNumbers: 'Jakákoli čísla',
        cardNumber: 'Číslo karty',
        cvcZip: 'CVC, PSČ',
        developmentMode: 'Vývojový režim',
        expirationDate: 'Datum platnosti',
        testCardInfo: 'Informace o testovací kartě',
      },
    },
    popular: 'Populární',
    pricingTable: {
      billingCycle: 'Fakturační cyklus',
      included: 'Zahrnuto',
    },
    reSubscribe: 'Znovu se přihlásit',
    seeAllFeatures: 'Zobrazit všechny funkce',
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: 'Přihlásit se',
    subscriptionDetails: {
      beginsOn: 'Začíná dne',
      currentBillingCycle: 'Aktuální fakturační cyklus',
      endsOn: 'Končí dne',
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: 'Výše další platby',
      nextPaymentOn: 'Další platba dne',
      pastDueAt: 'Po splatnosti dne',
      renewsAt: 'Obnovuje se dne',
      subscribedOn: 'Předplaceno dne',
      title: 'Předplatné',
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: 'Mezisoučet',
    switchPlan: 'Přepnout na tento plán',
    switchToAnnual: 'Přepnout na roční',
    switchToAnnualWithAnnualPrice: 'Přepnout na roční {{currency}}{{price}} / rok',
    switchToMonthly: 'Přepnout na měsíční',
    switchToMonthlyWithPrice: 'Přepnout na měsíční {{currency}}{{price}} / měsíc',
    totalDue: 'Celkem k zaplacení',
    totalDueToday: 'Celkem k zaplacení dnes',
    viewFeatures: 'Zobrazit funkce',
    viewPayment: undefined,
    year: 'Rok',
  },
  createOrganization: {
    formButtonSubmit: 'Vytvořit organizaci',
    invitePage: {
      formButtonReset: 'Přeskočit',
    },
    title: 'Vytvořit organizaci',
  },
  dates: {
    lastDay: "Včera v {{ date | timeString('cs-CZ') }}",
    next6Days: "Příští {{ date | weekday('cs-CZ','long') }} v {{ date | timeString('cs-CZ') }}",
    nextDay: "Zítra v {{ date | timeString('cs-CZ') }}",
    numeric: "{{ date | numeric('cs-CZ') }}",
    previous6Days: "Minulý {{ date | weekday('cs-CZ','long') }} v {{ date | timeString('cs-CZ') }}",
    sameDay: "Dnes v {{ date | timeString('cs-CZ') }}",
  },
  dividerText: 'nebo',
  footerActionLink__alternativePhoneCodeProvider: 'Místo toho poslat kód přes SMS',
  footerActionLink__useAnotherMethod: 'Použít jinou metodu',
  footerPageLink__help: 'Nápověda',
  footerPageLink__privacy: 'Ochrana soukromí',
  footerPageLink__terms: 'Podmínky',
  formButtonPrimary: 'Pokračovat',
  formButtonPrimary__verify: 'Ověřit',
  formFieldAction__forgotPassword: 'Zapomněli jste heslo?',
  formFieldError__matchingPasswords: 'Hesla se shodují.',
  formFieldError__notMatchingPasswords: 'Hesla se neshodují.',
  formFieldError__verificationLinkExpired: 'Ověřovací odkaz vypršel. Prosím, požádejte o nový odkaz.',
  formFieldHintText__optional: 'Volitelné',
  formFieldHintText__slug:
    'Slug je člověkem čitelný identifikátor, který musí být unikátní. Často se používá v URL adresách.',
  formFieldInputPlaceholder__apiKeyDescription: 'Vysvětlete, proč generujete tento klíč',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Vyberte datum',
  formFieldInputPlaceholder__apiKeyName: 'Zadejte název tajného klíče',
  formFieldInputPlaceholder__backupCode: 'Zadejte záložní kód',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Smazat účet',
  formFieldInputPlaceholder__emailAddress: 'Zadejte svou e-mailovou adresu',
  formFieldInputPlaceholder__emailAddress_username: 'Zadejte e-mail nebo uživatelské jméno',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'Křestní jméno',
  formFieldInputPlaceholder__lastName: 'Příjmení',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'vy@example.com',
  formFieldInputPlaceholder__organizationName: 'Název organizace',
  formFieldInputPlaceholder__organizationSlug: 'moje-org',
  formFieldInputPlaceholder__password: 'Zadejte své heslo',
  formFieldInputPlaceholder__phoneNumber: 'Zadejte své telefonní číslo',
  formFieldInputPlaceholder__username: undefined,
  formFieldLabel__apiKeyDescription: 'Popis',
  formFieldLabel__apiKeyExpiration: 'Platnost',
  formFieldLabel__apiKeyName: 'Název tajného klíče',
  formFieldLabel__automaticInvitations: 'Povolit automatické pozvánky pro tuto doménu',
  formFieldLabel__backupCode: 'Záložní kód',
  formFieldLabel__confirmDeletion: 'Potvrzení',
  formFieldLabel__confirmPassword: 'Potvrdit heslo',
  formFieldLabel__currentPassword: 'Aktuální heslo',
  formFieldLabel__emailAddress: 'E-mailová adresa',
  formFieldLabel__emailAddress_username: 'E-mailová adresa nebo uživatelské jméno',
  formFieldLabel__emailAddresses: 'E-mailové adresy',
  formFieldLabel__firstName: 'Křestní jméno',
  formFieldLabel__lastName: 'Příjmení',
  formFieldLabel__newPassword: 'Nové heslo',
  formFieldLabel__organizationDomain: 'Doména',
  formFieldLabel__organizationDomainDeletePending: 'Smazat čekající pozvánky a návrhy',
  formFieldLabel__organizationDomainEmailAddress: 'Ověřovací e-mailová adresa',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Zadejte e-mailovou adresu z této domény k obdržení kódu a ověření domény.',
  formFieldLabel__organizationName: 'Název',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Název přístupového klíče',
  formFieldLabel__password: 'Heslo',
  formFieldLabel__phoneNumber: 'Telefonní číslo',
  formFieldLabel__role: 'Role',
  formFieldLabel__signOutOfOtherSessions: 'Odhlásit se ze všech ostatních zařízení',
  formFieldLabel__username: 'Uživatelské jméno',
  impersonationFab: {
    action__signOut: 'Odhlásit se',
    title: 'Přihlášen jako {{identifier}}',
  },
  maintenanceMode: 'Momentálně provádíme údržbu, ale nebojte se, nemělo by to trvat déle než pár minut.',
  membershipRole__admin: 'Správce',
  membershipRole__basicMember: 'Člen',
  membershipRole__guestMember: 'Host',
  organizationList: {
    action__createOrganization: 'Vytvořit organizaci',
    action__invitationAccept: 'Připojit se',
    action__suggestionsAccept: 'Požádat o připojení',
    createOrganization: 'Vytvořit organizaci',
    invitationAcceptedLabel: 'Připojeno',
    subtitle: 'pro pokračování do {{applicationName}}',
    suggestionsAcceptedLabel: 'Čeká na schválení',
    title: 'Vyberte účet',
    titleWithoutPersonal: 'Vyberte organizaci',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API klíče',
    },
    badge__automaticInvitation: 'Automatické pozvánky',
    badge__automaticSuggestion: 'Automatické návrhy',
    badge__manualInvitation: 'Žádné automatické přihlášení',
    badge__unverified: 'Nepotvrzené',
    billingPage: {
      paymentHistorySection: {
        empty: 'Žádná historie plateb',
        notFound: 'Pokus o platbu nenalezen',
        tableHeader__amount: 'Částka',
        tableHeader__date: 'Datum',
        tableHeader__status: 'Stav',
      },
      paymentSourcesSection: {
        actionLabel__default: 'Nastavit jako výchozí',
        actionLabel__remove: 'Odebrat',
        add: 'Přidat novou platební metodu',
        addSubtitle: 'Přidejte novou platební metodu k vašemu účtu.',
        cancelButton: 'Zrušit',
        formButtonPrimary__add: 'Přidat platební metodu',
        formButtonPrimary__pay: 'Zaplatit {{amount}}',
        payWithTestCardButton: 'Zaplatit testovací kartou',
        removeResource: {
          messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
          messageLine2:
            'Tento platební zdroj již nebudete moci používat a veškerá opakující se předplatná, která na něm závisí, přestanou fungovat.',
          successMessage: '{{paymentSource}} byl odstraněn z vašeho účtu.',
          title: 'Odebrat platební metodu',
        },
        title: 'Platební metody',
      },
      start: {
        headerTitle__payments: 'Platby',
        headerTitle__plans: 'Plány',
        headerTitle__statements: 'Výpisy',
        headerTitle__subscriptions: 'Předplatné',
      },
      statementsSection: {
        empty: 'Žádné výpisy k zobrazení',
        itemCaption__paidForPlan: 'Zaplaceno za plán {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Poměrný kredit za částečné využití předchozího předplatného',
        itemCaption__subscribedAndPaidForPlan: 'Předplaceno a zaplaceno za plán {{plan}} {{period}}',
        notFound: 'Výpis nenalezen',
        tableHeader__amount: 'Částka',
        tableHeader__date: 'Datum',
        title: 'Výpisy',
        totalPaid: 'Celkem zaplaceno',
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: 'Přihlásit se k plánu',
        actionLabel__switchPlan: 'Změnit plány',
        tableHeader__edit: 'Upravit',
        tableHeader__plan: 'Plán',
        tableHeader__startDate: 'Datum zahájení',
        title: 'Předplatné',
      },
      subscriptionsSection: {
        actionLabel__default: 'Spravovat',
      },
      switchPlansSection: {
        title: 'Změnit plány',
      },
      title: 'Fakturace',
    },
    createDomainPage: {
      subtitle:
        'Přidejte doménu k ověření. Uživatelé s e-mailovými adresami z této domény se mohou připojit k organizaci automaticky nebo požádat o připojení.',
      title: 'Přidat doménu',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'Pozvánky nebylo možné odeslat. Již existují čekající pozvánky pro následující e-mailové adresy: {{email_addresses}}.',
      formButtonPrimary__continue: 'Odeslat pozvánky',
      selectDropdown__role: 'Vyberte roli',
      subtitle: 'Zadejte nebo vložte jednu nebo více e-mailových adres, oddělených mezerami nebo čárkami.',
      successMessage: 'Pozvánky úspěšně odeslány',
      title: 'Pozvat nové členy',
    },
    membersPage: {
      action__invite: 'Pozvat',
      action__search: 'Vyhledat',
      activeMembersTab: {
        menuAction__remove: 'Odebrat člena',
        tableHeader__actions: 'Akce',
        tableHeader__joined: 'Připojeno',
        tableHeader__role: 'Role',
        tableHeader__user: 'Uživatel',
      },
      detailsTitle__emptyRow: 'Žádní členové k zobrazení',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Pozvěte uživatele propojením e-mailové domény s vaší organizací. Kdokoli, kdo se zaregistruje s odpovídající e-mailovou doménou, se bude moci kdykoli připojit k organizaci.',
          headerTitle: 'Automatické pozvánky',
          primaryButton: 'Spravovat ověřené domény',
        },
        table__emptyRow: 'Žádné pozvánky k zobrazení',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Zrušit pozvánku',
        tableHeader__invited: 'Pozváni',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Uživatelé, kteří se zaregistrují s odpovídající e-mailovou doménou, uvidí návrh na připojení k vaší organizaci.',
          headerTitle: 'Automatické návrhy',
          primaryButton: 'Spravovat ověřené domény',
        },
        menuAction__approve: 'Schválit',
        menuAction__reject: 'Odmítnout',
        tableHeader__requested: 'Požádán přístup',
        table__emptyRow: 'Žádné požadavky k zobrazení',
      },
      start: {
        headerTitle__invitations: 'Pozvánky',
        headerTitle__members: 'Členové',
        headerTitle__requests: 'Žádosti',
      },
    },
    navbar: {
      apiKeys: 'API klíče',
      billing: 'Fakturace',
      description: 'Spravujte svou organizaci.',
      general: 'Obecné',
      members: 'Členové',
      title: 'Organizace',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Nemáte oprávnění spravovat fakturaci pro tuto organizaci.',
      },
      title: 'Plány',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Napište "{{organizationName}}" níže pro pokračování.',
          messageLine1: 'Jste si jisti, že chcete smazat tuto organizaci?',
          messageLine2: 'Tato akce je trvalá a nevratná.',
          successMessage: 'Organizací jste smazali.',
          title: 'Smazat organizaci',
        },
        leaveOrganization: {
          actionDescription: 'Napište "{{organizationName}}" níže pro pokračování.',
          messageLine1:
            'Jste si jisti, že chcete opustit tuto organizaci? Ztratíte přístup k této organizaci a jejím aplikacím.',
          messageLine2: 'Tato akce je trvalá a nevratná.',
          successMessage: 'Opustili jste organizaci.',
          title: 'Opustit organizaci',
        },
        title: 'Nebezpečí',
      },
      domainSection: {
        menuAction__manage: 'Spravovat',
        menuAction__remove: 'Smazat',
        menuAction__verify: 'Ověřit',
        primaryButton: 'Přidat doménu',
        subtitle:
          'Umožněte uživatelům připojit se k organizaci automaticky nebo požádat o připojení na základě ověřené e-mailové domény.',
        title: 'Ověřené domény',
      },
      successMessage: 'Organizace byla aktualizována.',
      title: 'Aktualizovat profil',
    },
    removeDomainPage: {
      messageLine1: 'E-mailová doména {{domain}} bude odstraněna.',
      messageLine2: 'Uživatelé se již nebudou moci automaticky připojit k organizaci po tomto.',
      successMessage: '{{domain}} byl odstraněn.',
      title: 'Odebrat doménu',
    },
    start: {
      headerTitle__general: 'Obecné',
      headerTitle__members: 'Členové',
      profileSection: {
        primaryButton: 'Aktualizovat profil',
        title: 'Profil organizace',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Odstranění této domény ovlivní pozvané uživatele.',
        removeDomainActionLabel__remove: 'Odebrat doménu',
        removeDomainSubtitle: 'Odebrat tuto doménu z vašich ověřených domén',
        removeDomainTitle: 'Odebrat doménu',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Uživatelé jsou automaticky zváni k připojení k organizaci při registraci a mohou se připojit kdykoli.',
        automaticInvitationOption__label: 'Automatické pozvánky',
        automaticSuggestionOption__description:
          'Uživatelé obdrží návrh na připojení, ale musí být schváleni administrátorem, než se budou moci připojit k organizaci.',
        automaticSuggestionOption__label: 'Automatické návrhy',
        calloutInfoLabel: 'Změna režimu registrace ovlivní pouze nové uživatele.',
        calloutInvitationCountLabel: 'Čekající pozvánky odeslané uživatelům: {{count}}',
        calloutSuggestionCountLabel: 'Čekající návrhy odeslané uživatelům: {{count}}',
        manualInvitationOption__description: 'Uživatelé mohou být do organizace pozváni pouze ručně.',
        manualInvitationOption__label: 'Žádné automatické přihlášení',
        subtitle: 'Vyberte, jak se uživatelé z této domény mohou připojit k organizaci.',
      },
      start: {
        headerTitle__danger: 'Nebezpečí',
        headerTitle__enrollment: 'Možnosti registrace',
      },
      subtitle: 'Doména {{domain}} je nyní ověřena. Pokračujte výběrem režimu registrace.',
      title: 'Aktualizovat {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Zadejte ověřovací kód odeslaný na vaši e-mailovou adresu',
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Doména {{domainName}} musí být ověřena e-mailem.',
      subtitleVerificationCodeScreen: 'Ověřovací kód byl odeslán na {{emailAddress}}. Zadejte kód pro pokračování.',
      title: 'Ověřit doménu',
    },
  },
  organizationSwitcher: {
    action__createOrganization: 'Vytvořit organizaci',
    action__invitationAccept: 'Připojit se',
    action__manageOrganization: 'Spravovat organizaci',
    action__suggestionsAccept: 'Požádat o připojení',
    notSelected: 'Není vybrána žádná organizace',
    personalWorkspace: 'Osobní účet',
    suggestionsAcceptedLabel: 'Čeká na schválení',
  },
  paginationButton__next: 'Další',
  paginationButton__previous: 'Předchozí',
  paginationRowText__displaying: 'Zobrazuji',
  paginationRowText__of: 'z',
  reverification: {
    alternativeMethods: {
      actionLink: 'Získat nápovědu',
      actionText: 'Nemáte žádnou z těchto možností?',
      blockButton__backupCode: 'Použít záložní kód',
      blockButton__emailCode: 'Odeslat kód na e-mail {{identifier}}',
      blockButton__passkey: 'Použít váš přístupový klíč',
      blockButton__password: 'Pokračovat s vaším heslem',
      blockButton__phoneCode: 'Odeslat SMS kód na {{identifier}}',
      blockButton__totp: 'Použít vaši aplikaci pro ověřování',
      getHelp: {
        blockButton__emailSupport: 'Podpora přes e-mail',
        content:
          'Pokud máte potíže s ověřením vašeho účtu, napište nám e-mail a my s vámi budeme spolupracovat na co nejrychlejším obnovení přístupu.',
        title: 'Získat nápovědu',
      },
      subtitle: 'Máte potíže? Můžete použít kteroukoli z těchto metod pro ověření.',
      title: 'Použít jinou metodu',
    },
    backupCodeMfa: {
      subtitle: 'Zadejte záložní kód, který jste obdrželi při nastavení dvoufázové autentizace',
      title: 'Zadejte záložní kód',
    },
    emailCode: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte kód odeslaný na váš e-mail pro pokračování',
      title: 'Vyžadováno ověření',
    },
    noAvailableMethods: {
      message: 'Nelze pokračovat s ověřením. Není nakonfigurován žádný vhodný autentizační faktor',
      subtitle: 'Došlo k chybě',
      title: 'Nelze ověřit váš účet',
    },
    passkey: {
      blockButton__passkey: 'Použít váš přístupový klíč',
      subtitle:
        'Použití vašeho přístupového klíče potvrzuje vaši identitu. Vaše zařízení může požádat o otisk prstu, obličej nebo zámek obrazovky.',
      title: 'Použít váš přístupový klíč',
    },
    password: {
      actionLink: 'Použít jinou metodu',
      subtitle: 'Zadejte své aktuální heslo pro pokračování',
      title: 'Vyžadováno ověření',
    },
    phoneCode: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte kód odeslaný na váš telefon pro pokračování',
      title: 'Vyžadováno ověření',
    },
    phoneCodeMfa: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte kód odeslaný na váš telefon pro pokračování',
      title: 'Vyžadováno ověření',
    },
    totpMfa: {
      formTitle: 'Ověřovací kód',
      subtitle: 'Zadejte kód vygenerovaný vaší aplikací pro ověřování pro pokračování',
      title: 'Vyžadováno ověření',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Přidat účet',
      action__signOutAll: 'Odhlásit se ze všech účtů',
      subtitle: 'Vyberte účet, se kterým chcete pokračovat.',
      title: 'Vyberte účet',
    },
    alternativeMethods: {
      actionLink: 'Získat nápovědu',
      actionText: 'Nemáte žádnou z těchto možností?',
      blockButton__backupCode: 'Použít záložní kód',
      blockButton__emailCode: 'Odeslat e-mailový kód na {{identifier}}',
      blockButton__emailLink: 'Odeslat e-mailový odkaz na {{identifier}}',
      blockButton__passkey: 'Přihlásit se pomocí vašeho přístupového klíče',
      blockButton__password: 'Přihlásit se pomocí vašeho hesla',
      blockButton__phoneCode: 'Odeslat SMS kód na {{identifier}}',
      blockButton__totp: 'Použít vaši aplikaci pro ověřování',
      getHelp: {
        blockButton__emailSupport: 'Podpora přes e-mail',
        content:
          'Pokud máte potíže s přihlášením do vašeho účtu, napište nám e-mail a my s vámi budeme spolupracovat na co nejrychlejším obnovení přístupu.',
        title: 'Získat nápovědu',
      },
      subtitle: 'Máte potíže? Můžete použít kteroukoli z těchto metod k přihlášení.',
      title: 'Použít jinou metodu',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'pro pokračování do {{applicationName}}',
      title: 'Zkontrolujte svůj {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'Váš záložní kód je ten, který jste získali při nastavení dvoufázové autentizace.',
      title: 'Zadejte záložní kód',
    },
    emailCode: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'pro pokračování do {{applicationName}}',
      title: 'Zkontrolujte svůj e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Pro pokračování otevřete ověřovací odkaz na zařízení a prohlížeči, ze kterého jste zahájili přihlášení',
        title: 'Ověřovací odkaz je pro toto zařízení neplatný',
      },
      expired: {
        subtitle: 'Vraťte se do původní karty pro pokračování.',
        title: 'Tento ověřovací odkaz vypršel',
      },
      failed: {
        subtitle: 'Vraťte se do původní karty pro pokračování.',
        title: 'Tento ověřovací odkaz je neplatný',
      },
      formSubtitle: 'Použijte ověřovací odkaz odeslaný na váš e-mail',
      formTitle: 'Ověřovací odkaz',
      loading: {
        subtitle: 'Brzy budete přesměrováni',
        title: 'Přihlašování...',
      },
      resendButton: 'Neobdrželi jste odkaz? Znovu poslat',
      subtitle: 'pro pokračování do {{applicationName}}',
      title: 'Zkontrolujte svůj e-mail',
      unusedTab: {
        title: 'Můžete zavřít tuto kartu',
      },
      verified: {
        subtitle: 'Brzy budete přesměrováni',
        title: 'Úspěšně přihlášeno',
      },
      verifiedSwitchTab: {
        subtitle: 'Vraťte se na původní kartu pro pokračování',
        subtitleNewTab: 'Vraťte se na nově otevřenou kartu pro pokračování',
        titleNewTab: 'Přihlášeno na jiné kartě',
      },
    },
    forgotPassword: {
      formTitle: 'Kód pro resetování hesla',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'pro resetování hesla',
      subtitle_email: 'Nejprve zadejte kód odeslaný na váš e-mail',
      subtitle_phone: 'Nejprve zadejte kód odeslaný na váš telefon',
      title: 'Resetovat heslo',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Resetovat heslo',
      label__alternativeMethods: 'Nebo se přihlaste jinou metodou',
      title: 'Zapomněli jste heslo?',
    },
    noAvailableMethods: {
      message: 'Nelze pokračovat v přihlášení. Není k dispozici žádný autentizační faktor.',
      subtitle: 'Došlo k chybě',
      title: 'Nelze se přihlásit',
    },
    passkey: {
      subtitle:
        'Použití vašeho přístupového klíče potvrzuje, že jste to vy. Vaše zařízení může požádat o otisk prstu, obličej nebo zámek obrazovky.',
      title: 'Použít váš přístupový klíč',
    },
    password: {
      actionLink: 'Použít jinou metodu',
      subtitle: 'Zadejte heslo spojené s vaším účtem',
      title: 'Zadejte své heslo',
    },
    passwordPwned: {
      title: 'Heslo kompromitováno',
    },
    phoneCode: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'pro pokračování do {{applicationName}}',
      title: 'Zkontrolujte svůj telefon',
    },
    phoneCodeMfa: {
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Pro pokračování zadejte ověřovací kód odeslaný na váš telefon',
      title: 'Zkontrolujte svůj telefon',
    },
    resetPassword: {
      formButtonPrimary: 'Resetovat heslo',
      requiredMessage: 'Z bezpečnostních důvodů je nutné resetovat vaše heslo.',
      successMessage: 'Vaše heslo bylo úspěšně změněno. Přihlašuji vás, prosím počkejte okamžik.',
      title: 'Nastavit nové heslo',
    },
    resetPasswordMfa: {
      detailsLabel: 'Před resetováním hesla musíme ověřit vaši identitu.',
    },
    start: {
      actionLink: 'Registrovat se',
      actionLink__join_waitlist: 'Připojit se k čekací listině',
      actionLink__use_email: 'Použít e-mail',
      actionLink__use_email_username: 'Použít e-mail nebo uživatelské jméno',
      actionLink__use_passkey: 'Použít přístupový klíč místo toho',
      actionLink__use_phone: 'Použít telefon',
      actionLink__use_username: 'Použít uživatelské jméno',
      actionText: 'Nemáte účet?',
      actionText__join_waitlist: 'Chcete dřívější přístup?',
      alternativePhoneCodeProvider: {
        actionLink: 'Použít jinou metodu',
        label: 'Telefonní číslo {{provider}}',
        subtitle: 'Zadejte své telefonní číslo, abyste získali ověřovací kód na {{provider}}.',
        title: 'Přihlásit se do {{applicationName}} pomocí {{provider}}',
      },
      subtitle: 'Vítejte zpět! Prosím přihlaste se pro pokračování',
      subtitleCombined: undefined,
      title: 'Přihlásit se do {{applicationName}}',
      titleCombined: 'Pokračovat do {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Ověřovací kód',
      subtitle: 'Pro pokračování zadejte ověřovací kód vygenerovaný vaší aplikací pro ověřování',
      title: 'Dvoufázové ověření',
    },
  },
  signInEnterPasswordTitle: 'Zadejte své heslo',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte ověřovací kód odeslaný na váš {{provider}}',
      title: 'Ověřte svůj {{provider}}',
    },
    continue: {
      actionLink: 'Přihlásit se',
      actionText: 'Už máte účet?',
      subtitle: 'Prosím vyplňte zbývající údaje pro pokračování.',
      title: 'Vyplňte chybějící pole',
    },
    emailCode: {
      formSubtitle: 'Zadejte ověřovací kód odeslaný na vaši e-mailovou adresu',
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte ověřovací kód odeslaný na váš e-mail',
      title: 'Ověřte svůj e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Pro pokračování otevřete ověřovací odkaz na zařízení a prohlížeči, ze kterého jste zahájili registraci',
        title: 'Ověřovací odkaz je pro toto zařízení neplatný',
      },
      formSubtitle: 'Použijte ověřovací odkaz odeslaný na vaši e-mailovou adresu',
      formTitle: 'Ověřovací odkaz',
      loading: {
        title: 'Registruji se...',
      },
      resendButton: 'Neobdrželi jste odkaz? Znovu poslat',
      subtitle: 'pro pokračování do {{applicationName}}',
      title: 'Ověřte svůj e-mail',
      verified: {
        title: 'Úspěšně zaregistrováno',
      },
      verifiedSwitchTab: {
        subtitle: 'Vraťte se na nově otevřenou kartu pro pokračování',
        subtitleNewTab: 'Vraťte se na předchozí kartu pro pokračování',
        title: 'E-mail úspěšně ověřen',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Souhlasím s {{ privacyPolicyLink || link("Zásadami ochrany osobních údajů") }}',
        label__onlyTermsOfService: 'Souhlasím s {{ termsOfServiceLink || link("Podmínkami služby") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Souhlasím s {{ termsOfServiceLink || link("Podmínkami služby") }} a {{ privacyPolicyLink || link("Zásadami ochrany osobních údajů") }}',
      },
      continue: {
        subtitle: 'Prosím přečtěte si a přijměte podmínky pro pokračování',
        title: 'Právní souhlas',
      },
    },
    phoneCode: {
      formSubtitle: 'Zadejte ověřovací kód odeslaný na vaše telefonní číslo',
      formTitle: 'Ověřovací kód',
      resendButton: 'Neobdrželi jste kód? Znovu poslat',
      subtitle: 'Zadejte ověřovací kód odeslaný na váš telefon',
      title: 'Ověřte svůj telefon',
    },
    restrictedAccess: {
      actionLink: 'Přihlásit se',
      actionText: 'Už máte účet?',
      blockButton__emailSupport: 'Podpora přes e-mail',
      blockButton__joinWaitlist: 'Připojit se k čekací listině',
      subtitle:
        'Registrace jsou momentálně zakázány. Pokud se domníváte, že byste měli mít přístup, kontaktujte prosím podporu.',
      subtitleWaitlist:
        'Registrace jsou momentálně zakázány. Chcete-li být první, kdo se dozví, kdy spustíme, připojte se k čekací listině.',
      title: 'Omezený přístup',
    },
    start: {
      actionLink: 'Přihlásit se',
      actionLink__use_email: 'Použít e-mail místo toho',
      actionLink__use_phone: 'Použít telefon místo toho',
      actionText: 'Už máte účet?',
      alternativePhoneCodeProvider: {
        actionLink: 'Použít jinou metodu',
        label: 'Telefonní číslo {{provider}}',
        subtitle: 'Zadejte své telefonní číslo, abyste získali ověřovací kód na {{provider}}.',
        title: 'Zaregistrovat se do {{applicationName}} pomocí {{provider}}',
      },
      subtitle: 'Vítejte! Prosím vyplňte údaje pro začátek.',
      subtitleCombined: 'Vítejte! Prosím vyplňte údaje pro začátek.',
      title: 'Vytvořte si účet',
      titleCombined: 'Vytvořte si účet',
    },
  },
  socialButtonsBlockButton: 'Pokračovat s {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    chooseOrganization: {
      action__createOrganization: undefined,
      action__invitationAccept: undefined,
      action__suggestionsAccept: undefined,
      subtitle: undefined,
      suggestionsAcceptedLabel: undefined,
      title: undefined,
    },
    createOrganization: {
      formButtonReset: undefined,
      formButtonSubmit: undefined,
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: undefined,
      title: undefined,
    },
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} je již členem organizace.',
    captcha_invalid: undefined,
    captcha_unavailable:
      'Registrace nebyla úspěšná kvůli neúspěšné validaci bota. Prosím obnovte stránku a zkuste to znovu, nebo se obraťte na podporu pro další pomoc.',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: undefined,
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: undefined,
    form_param_format_invalid__phone_number: undefined,
    form_param_max_length_exceeded__first_name: undefined,
    form_param_max_length_exceeded__last_name: undefined,
    form_param_max_length_exceeded__name: undefined,
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'Vaše heslo je příliš krátké. Musí mít alespoň 8 znaků.',
    form_password_not_strong_enough: 'Vaše heslo není dostatečně silné.',
    form_password_pwned: 'Toto heslo bylo nalezeno jako součást prolomení a nelze ho použít, zkuste prosím jiné heslo.',
    form_password_pwned__sign_in:
      'Toto heslo bylo nalezeno jako součást prolomení a nelze ho použít, prosím resetujte si heslo.',
    form_password_size_in_bytes_exceeded: undefined,
    form_password_validation_failed: undefined,
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'Vaše uživatelské jméno musí mít mezi {{min_length}} a {{max_length}} znaky.',
    identification_deletion_failed: undefined,
    not_allowed_access: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'Přístupový klíč je již registrován na tomto zařízení.',
    passkey_not_supported: 'Přístupové klíče nejsou podporovány na tomto zařízení.',
    passkey_pa_not_supported: 'Registrace vyžaduje autentizační metodu platformy, ale zařízení ji nepodporuje.',
    passkey_registration_cancelled: 'Registrace přístupového klíče byla zrušena nebo vypršel časový limit.',
    passkey_retrieval_cancelled: 'Ověření přístupového klíče bylo zrušeno nebo vypršel časový limit.',
    passwordComplexity: {
      maximumLength: 'méně než {{length}} znaků',
      minimumLength: '{{length}} nebo více znaků',
      requireLowercase: 'malé písmeno',
      requireNumbers: 'číslici',
      requireSpecialCharacter: 'speciální znak',
      requireUppercase: 'velké písmeno',
      sentencePrefix: 'Vaše heslo musí obsahovat',
    },
    phone_number_exists: undefined,
    session_exists: undefined,
    web3_missing_identifier: 'Rozšíření peněženky Web3 nebylo nalezeno. Pro pokračování prosím nainstalujte jednu.',
    zxcvbn: {
      couldBeStronger: 'Vaše heslo funguje, ale mohlo by být silnější. Zkuste přidat více znaků.',
      goodPassword: 'Vaše heslo splňuje všechny potřebné požadavky.',
      notEnough: 'Vaše heslo není dostatečně silné.',
      suggestions: {
        allUppercase: 'Použijte velká písmena u některých, ale ne všech písmen.',
        anotherWord: 'Přidejte více slov, která jsou méně běžná.',
        associatedYears: 'Vyhněte se rokům, které jsou s vámi spojeny.',
        capitalization: 'Použijte velká písmena více než jen u prvního písmene.',
        dates: 'Vyhněte se datům a rokům, které jsou s vámi spojeny.',
        l33t: "Vyhněte se předvídatelným náhradám písmen jako '@' za 'a'.",
        longerKeyboardPattern: 'Použijte delší klávesnicové vzory a několikrát změňte směr psaní.',
        noNeed: 'Můžete vytvářet silná hesla bez použití symbolů, čísel nebo velkých písmen.',
        pwned: 'Pokud používáte toto heslo jinde, měli byste ho změnit.',
        recentYears: 'Vyhněte se nedávným rokům.',
        repeated: 'Vyhněte se opakujícím se slovům a znakům.',
        reverseWords: 'Vyhněte se obráceným pravopisům běžných slov.',
        sequences: 'Vyhněte se běžným sekvencím znaků.',
        useWords: 'Použijte více slov, ale vyhněte se běžným frázím.',
      },
      warnings: {
        common: 'Toto je běžně používané heslo.',
        commonNames: 'Běžná jména a příjmení jsou snadno uhádnutelná.',
        dates: 'Data jsou snadno uhádnutelná.',
        extendedRepeat: 'Opakované vzory znaků jako "abcabcabc" jsou snadno uhádnutelné.',
        keyPattern: 'Krátké klávesnicové vzory jsou snadno uhádnutelné.',
        namesByThemselves: 'Samotná jména nebo příjmení jsou snadno uhádnutelná.',
        pwned: 'Vaše heslo bylo odhaleno datovým únikem na internetu.',
        recentYears: 'Nedávné roky jsou snadno uhádnutelné.',
        sequences: 'Běžné sekvence znaků jako "abc" jsou snadno uhádnutelné.',
        similarToCommon: 'Toto je podobné běžně používanému heslu.',
        simpleRepeat: 'Opakované znaky jako "aaa" jsou snadno uhádnutelné.',
        straightRow: 'Přímé řady kláves na klávesnici jsou snadno uhádnutelné.',
        topHundred: 'Toto je často používané heslo.',
        topTen: 'Toto je velmi často používané heslo.',
        userInputs: 'Neměly by zde být žádné osobní nebo se stránkou související údaje.',
        wordByItself: 'Jednotlivá slova jsou snadno uhádnutelná.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Přidat účet',
    action__manageAccount: 'Spravovat účet',
    action__signOut: 'Odhlásit se',
    action__signOutAll: 'Odhlásit se ze všech účtů',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API klíče',
    },
    backupCodePage: {
      actionLabel__copied: 'Zkopírováno!',
      actionLabel__copy: 'Zkopírovat vše',
      actionLabel__download: 'Stáhnout .txt',
      actionLabel__print: 'Vytisknout',
      infoText1: 'Záložní kódy budou pro tento účet povoleny.',
      infoText2:
        'Uchovávejte záložní kódy v tajnosti a bezpečně. Můžete vygenerovat nové záložní kódy, pokud máte podezření, že byly kompromitovány.',
      subtitle__codelist: 'Uchovávejte je bezpečně a tajně.',
      successMessage:
        'Záložní kódy jsou nyní povoleny. Můžete použít jeden z těchto kódů k přihlášení do svého účtu, pokud ztratíte přístup k vašemu ověřovacímu zařízení. Každý kód lze použít pouze jednou.',
      successSubtitle:
        'Můžete použít jeden z těchto kódů k přihlášení do svého účtu, pokud ztratíte přístup k vašemu ověřovacímu zařízení.',
      title: 'Přidat ověřování pomocí záložních kódů',
      title__codelist: 'Záložní kódy',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Žádná historie plateb',
        notFound: 'Pokus o platbu nenalezen',
        tableHeader__amount: 'Částka',
        tableHeader__date: 'Datum',
        tableHeader__status: 'Stav',
      },
      paymentSourcesSection: {
        actionLabel__default: 'Nastavit jako výchozí',
        actionLabel__remove: 'Odebrat',
        add: 'Přidat novou platební metodu',
        addSubtitle: 'Přidejte novou platební metodu k vašemu účtu.',
        cancelButton: 'Zrušit',
        formButtonPrimary__add: 'Přidat platební metodu',
        formButtonPrimary__pay: 'Zaplatit {{amount}}',
        payWithTestCardButton: 'Zaplatit testovací kartou',
        removeResource: {
          messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
          messageLine2:
            'Tento platební zdroj již nebudete moci používat a veškerá opakující se předplatná, která na něm závisí, přestanou fungovat.',
          successMessage: '{{paymentSource}} byl odstraněn z vašeho účtu.',
          title: 'Odebrat platební metodu',
        },
        title: 'Platební metody',
      },
      start: {
        headerTitle__payments: 'Platby',
        headerTitle__plans: 'Plány',
        headerTitle__statements: 'Výpisy',
        headerTitle__subscriptions: 'Předplatné',
      },
      statementsSection: {
        empty: 'Žádné výpisy k zobrazení',
        itemCaption__paidForPlan: 'Zaplaceno za plán {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Poměrný kredit za částečné využití předchozího předplatného',
        itemCaption__subscribedAndPaidForPlan: 'Předplaceno a zaplaceno za plán {{plan}} {{period}}',
        notFound: 'Výpis nenalezen',
        tableHeader__amount: 'Částka',
        tableHeader__date: 'Datum',
        title: 'Výpisy',
        totalPaid: 'Celkem zaplaceno',
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: 'Přihlásit se k plánu',
        actionLabel__switchPlan: 'Změnit plány',
        tableHeader__edit: 'Upravit',
        tableHeader__plan: 'Plán',
        tableHeader__startDate: 'Datum zahájení',
        title: 'Předplatné',
      },
      subscriptionsSection: {
        actionLabel__default: 'Spravovat',
      },
      switchPlansSection: {
        title: 'Změnit plány',
      },
      title: 'Fakturace',
    },
    connectedAccountPage: {
      formHint: 'Vyberte poskytovatele pro připojení vašeho účtu.',
      formHint__noAccounts: 'Nejsou k dispozici žádní externí poskytovatelé účtů.',
      removeResource: {
        messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
        messageLine2: 'Již nebudete moci používat tento připojený účet a veškeré závislé funkce přestanou fungovat.',
        successMessage: '{{connectedAccount}} byl odstraněn z vašeho účtu.',
        title: 'Odebrat připojený účet',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'Poskytovatel byl přidán k vašemu účtu',
      title: 'Přidat připojený účet',
    },
    deletePage: {
      actionDescription: 'Napište "Smazat účet" níže pro pokračování.',
      confirm: 'Smazat účet',
      messageLine1: 'Jste si jisti, že chcete smazat svůj účet?',
      messageLine2: 'Tato akce je trvalá a nevratná.',
      title: 'Smazat účet',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Na tuto e-mailovou adresu bude odeslán ověřovací kód.',
        formSubtitle: 'Zadejte ověřovací kód odeslaný na {{identifier}}',
        formTitle: 'Ověřovací kód',
        resendButton: 'Neobdrželi jste kód? Znovu poslat',
        successMessage: 'E-mail {{identifier}} byl přidán k vašemu účtu.',
      },
      emailLink: {
        formHint: 'Na tuto e-mailovou adresu bude odeslán ověřovací odkaz.',
        formSubtitle: 'Klikněte na ověřovací odkaz v e-mailu odeslaném na {{identifier}}',
        formTitle: 'Ověřovací odkaz',
        resendButton: 'Neobdrželi jste odkaz? Znovu poslat',
        successMessage: 'E-mail {{identifier}} byl přidán k vašemu účtu.',
      },
      enterpriseSSOLink: {
        formButton: 'Klikněte pro přihlášení',
        formSubtitle: 'Dokončete přihlášení pomocí {{identifier}}',
      },
      formHint: 'Tuto e-mailovou adresu budete muset ověřit, než ji bude možné přidat k vašemu účtu.',
      removeResource: {
        messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
        messageLine2: 'Již nebudete moci přihlásit se pomocí této e-mailové adresy.',
        successMessage: '{{emailAddress}} byl odstraněn z vašeho účtu.',
        title: 'Odebrat e-mailovou adresu',
      },
      title: 'Přidat e-mailovou adresu',
      verifyTitle: 'Ověřit e-mailovou adresu',
    },
    formButtonPrimary__add: 'Přidat',
    formButtonPrimary__continue: 'Pokračovat',
    formButtonPrimary__finish: 'Dokončit',
    formButtonPrimary__remove: 'Odebrat',
    formButtonPrimary__save: 'Uložit',
    formButtonReset: 'Zrušit',
    mfaPage: {
      formHint: 'Vyberte metodu k přidání.',
      title: 'Přidat dvoufázové ověření',
    },
    mfaPhoneCodePage: {
      backButton: 'Použít stávající číslo',
      primaryButton__addPhoneNumber: 'Přidat telefonní číslo',
      removeResource: {
        messageLine1: '{{identifier}} již nebude dostávat ověřovací kódy při přihlašování.',
        messageLine2: 'Váš účet nemusí být tak bezpečný. Jste si jisti, že chcete pokračovat?',
        successMessage: 'Dvoufázové ověření SMS kódem bylo odstraněno pro {{mfaPhoneCode}}',
        title: 'Odebrat dvoufázové ověření',
      },
      subtitle__availablePhoneNumbers:
        'Vyberte stávající telefonní číslo pro registraci dvoufázového ověření SMS kódem nebo přidejte nové.',
      subtitle__unavailablePhoneNumbers:
        'Nejsou k dispozici žádná telefonní čísla pro registraci dvoufázového ověření SMS kódem, prosím přidejte nové.',
      successMessage1:
        'Při přihlášení budete muset jako další krok zadat ověřovací kód odeslaný na toto telefonní číslo.',
      successMessage2:
        'Uložte si tyto záložní kódy a uložte je na bezpečné místo. Pokud ztratíte přístup ke svému ověřovacímu zařízení, můžete k přihlášení použít záložní kódy.',
      successTitle: 'SMS kódové ověření povoleno',
      title: 'Přidat ověření SMS kódem',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Místo toho naskenovat QR kód',
        buttonUnableToScan__nonPrimary: 'Nemůžete naskenovat QR kód?',
        infoText__ableToScan:
          'Nastavte novou metodu přihlášení ve vaší aplikaci pro ověřování a naskenujte následující QR kód, abyste ji propojili se svým účtem.',
        infoText__unableToScan:
          'Nastavte novou metodu přihlášení ve své aplikaci pro ověřování a zadejte níže uvedený klíč.',
        inputLabel__unableToScan1:
          'Ujistěte se, že je povoleno časové nebo jednorázové heslo, a dokončete propojení svého účtu.',
        inputLabel__unableToScan2:
          'Alternativně, pokud vaše aplikace pro ověřování podporuje TOTP URI, můžete také zkopírovat celý URI.',
      },
      removeResource: {
        messageLine1: 'Ověřovací kódy z této aplikace pro ověřování již nebudou vyžadovány při přihlašování.',
        messageLine2: 'Váš účet nemusí být tak bezpečný. Jste si jisti, že chcete pokračovat?',
        successMessage: 'Dvoufázové ověření pomocí aplikace pro ověřování bylo odstraněno.',
        title: 'Odebrat dvoufázové ověření',
      },
      successMessage:
        'Dvoufázové ověření je nyní povoleno. Při přihlášení budete muset zadat ověřovací kód z této aplikace pro ověřování jako další krok.',
      title: 'Přidat aplikaci pro ověřování',
      verifySubtitle: 'Zadejte ověřovací kód vygenerovaný vaší aplikací pro ověřování',
      verifyTitle: 'Ověřovací kód',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profil',
      apiKeys: 'API klíče',
      billing: 'Fakturace',
      description: 'Spravujte informace o svém účtu.',
      security: 'Zabezpečení',
      title: 'Účet',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} bude odstraněn z tohoto účtu.',
        title: 'Odebrat přístupový klíč',
      },
      subtitle__rename: 'Můžete změnit název přístupového klíče, aby se snáze našel.',
      title__rename: 'Přejmenovat přístupový klíč',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Doporučuje se odhlásit se ze všech ostatních zařízení, která mohla použít vaše staré heslo.',
      readonly:
        'Vaše heslo nelze aktuálně upravit, protože se můžete přihlásit pouze prostřednictvím podnikového připojení.',
      successMessage__set: 'Vaše heslo bylo nastaveno.',
      successMessage__signOutOfOtherSessions: 'Všechna ostatní zařízení byla odhlášena.',
      successMessage__update: 'Vaše heslo bylo aktualizováno.',
      title__set: 'Nastavit heslo',
      title__update: 'Aktualizovat heslo',
    },
    phoneNumberPage: {
      infoText:
        'Na toto telefonní číslo bude odeslána textová zpráva obsahující ověřovací kód. Mohou být účtovány poplatky za zprávy a data.',
      removeResource: {
        messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
        messageLine2: 'Již nebudete moci přihlásit se pomocí tohoto telefonního čísla.',
        successMessage: '{{phoneNumber}} byl odstraněn z vašeho účtu.',
        title: 'Odebrat telefonní číslo',
      },
      successMessage: '{{identifier}} byl přidán k vašemu účtu.',
      title: 'Přidat telefonní číslo',
      verifySubtitle: 'Zadejte ověřovací kód odeslaný na {{identifier}}',
      verifyTitle: 'Ověřit telefonní číslo',
    },
    plansPage: {
      title: 'Plány',
    },
    profilePage: {
      fileDropAreaHint: 'Doporučená velikost 1:1, až 10MB.',
      imageFormDestructiveActionSubtitle: 'Odebrat',
      imageFormSubtitle: 'Nahrát',
      imageFormTitle: 'Profilový obrázek',
      readonly: 'Informace o vašem profilu byly poskytnuty podnikovým připojením a nelze je upravovat.',
      successMessage: 'Váš profil byl aktualizován.',
      title: 'Aktualizovat profil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Odhlásit se ze zařízení',
        title: 'Aktivní zařízení',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Znovu připojit',
        actionLabel__reauthorize: 'Autorizovat nyní',
        destructiveActionTitle: 'Odebrat',
        primaryButton: 'Připojit účet',
        subtitle__disconnected: 'Tento účet byl odpojen.',
        subtitle__reauthorize:
          'Požadované rozsahy byly aktualizovány a můžete zaznamenat omezenou funkcionalitu. Prosím znovu autorizujte tuto aplikaci, abyste se vyhnuli problémům',
        title: 'Připojené účty',
      },
      dangerSection: {
        deleteAccountButton: 'Smazat účet',
        title: 'Smazat účet',
      },
      emailAddressesSection: {
        destructiveAction: 'Odebrat e-mail',
        detailsAction__nonPrimary: 'Nastavit jako primární',
        detailsAction__primary: 'Dokončit ověření',
        detailsAction__unverified: 'Ověřit',
        primaryButton: 'Přidat e-mailovou adresu',
        title: 'E-mailové adresy',
      },
      enterpriseAccountsSection: {
        title: 'Podnikové účty',
      },
      headerTitle__account: 'Podrobnosti profilu',
      headerTitle__security: 'Zabezpečení',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Znovu vygenerovat',
          headerTitle: 'Záložní kódy',
          subtitle__regenerate:
            'Získejte novou sadu zabezpečených záložních kódů. Předchozí záložní kódy budou smazány a nelze je použít.',
          title__regenerate: 'Znovu vygenerovat záložní kódy',
        },
        phoneCode: {
          actionLabel__setDefault: 'Nastavit jako výchozí',
          destructiveActionLabel: 'Odebrat',
        },
        primaryButton: 'Přidat dvoufázové ověření',
        title: 'Dvoufázové ověření',
        totp: {
          destructiveActionTitle: 'Odebrat',
          headerTitle: 'Aplikace pro ověřování',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Odebrat',
        menuAction__rename: 'Přejmenovat',
        primaryButton: 'Přidat přístupový klíč',
        title: 'Přístupové klíče',
      },
      passwordSection: {
        primaryButton__setPassword: 'Nastavit heslo',
        primaryButton__updatePassword: 'Aktualizovat heslo',
        title: 'Heslo',
      },
      phoneNumbersSection: {
        destructiveAction: 'Odebrat telefonní číslo',
        detailsAction__nonPrimary: 'Nastavit jako primární',
        detailsAction__primary: 'Dokončit ověření',
        detailsAction__unverified: 'Ověřit telefonní číslo',
        primaryButton: 'Přidat telefonní číslo',
        title: 'Telefonní čísla',
      },
      profileSection: {
        primaryButton: 'Aktualizovat profil',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Nastavit uživatelské jméno',
        primaryButton__updateUsername: 'Aktualizovat uživatelské jméno',
        title: 'Uživatelské jméno',
      },
      web3WalletsSection: {
        destructiveAction: 'Odebrat peněženku',
        detailsAction__nonPrimary: 'Nastavit jako primární',
        primaryButton: 'Připojit peněženku',
        title: 'Web3 peněženky',
      },
    },
    usernamePage: {
      successMessage: 'Vaše uživatelské jméno bylo aktualizováno.',
      title__set: 'Nastavit uživatelské jméno',
      title__update: 'Aktualizovat uživatelské jméno',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} bude odstraněn z tohoto účtu.',
        messageLine2: 'Již se nebudete moci přihlásit pomocí této web3 peněženky.',
        successMessage: '{{web3Wallet}} byl odstraněn z vašeho účtu.',
        title: 'Odebrat web3 peněženku',
      },
      subtitle__availableWallets: 'Vyberte web3 peněženku k připojení k vašemu účtu.',
      subtitle__unavailableWallets: 'Nejsou k dispozici žádné web3 peněženky.',
      successMessage: 'Peněženka byla přidána k vašemu účtu.',
      title: 'Přidat web3 peněženku',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Přihlásit se',
      actionText: 'Už máte přístup?',
      formButton: 'Připojit se k čekací listině',
      subtitle: 'Zadejte svou e-mailovou adresu a dáme vám vědět, až bude vaše místo připraveno',
      title: 'Připojit se k čekací listině',
    },
    success: {
      message: 'Brzy budete přesměrováni...',
      subtitle: 'Ozveme se vám, až bude vaše místo připraveno',
      title: 'Děkujeme za připojení k čekací listině!',
    },
  },
} as const;
